import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LS_KEY_GUEST_FAVS = 'huepfburgen_guest_favs';
const LS_KEY_COMPARE = 'huepfburgen_compare';
const MAX_COMPARE = 3;

const FavoritesContext = createContext(null);

export const useFavorites = () => {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error('useFavorites must be used inside <FavoritesProvider>');
  return ctx;
};

/**
 * Provides:
 *  - favourites: list of product ids the customer marked with ❤
 *  - compareIds: list of product ids the visitor has flagged for comparison
 * Persists guest favs/compare to localStorage; merges with server-side favourites
 * automatically as soon as the customer logs in.
 */
export const FavoritesProvider = ({ children }) => {
  const [favIds, setFavIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY_GUEST_FAVS) || '[]'); } catch { return []; }
  });
  const [compareIds, setCompareIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY_COMPARE) || '[]'); } catch { return []; }
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // True wenn der initiale Server-Merge bereits durchgeführt wurde.
  // Verhindert, dass spätere reload-Aufrufe gelöschte Favoriten via Merge wieder hochladen
  // (stale-closure Bug, der das „Entfernen" wirkungslos machte).
  const hydratedRef = useRef(false);

  // Erstmalige Hydrierung vom Server inkl. Merge der localStorage-Favs (Gast → Login).
  const hydrateFromServer = useCallback(async (localFavs) => {
    try {
      const res = await axios.get(`${API}/me/favorites`, { withCredentials: true });
      const serverIds = (res.data?.favorites || []).map((p) => p.id);
      setIsAuthenticated(true);

      // Merge nur einmal beim ersten Hydrate – verhindert Re-Upload gelöschter IDs.
      if (!hydratedRef.current) {
        const localOnly = (localFavs || []).filter((id) => !serverIds.includes(id));
        for (const pid of localOnly) {
          try {
            await axios.post(`${API}/me/favorites`, { product_id: pid }, { withCredentials: true });
            serverIds.push(pid);
          } catch { /* ignore individual failures */ }
        }
        localStorage.removeItem(LS_KEY_GUEST_FAVS);
        hydratedRef.current = true;
      }
      setFavIds(serverIds);
    } catch {
      setIsAuthenticated(false);
    }
  }, []);

  // Reines Neuladen ohne Merge — sicher nach Toggle-Aktionen.
  const reloadFromServer = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/me/favorites`, { withCredentials: true });
      const serverIds = (res.data?.favorites || []).map((p) => p.id);
      setIsAuthenticated(true);
      setFavIds(serverIds);
    } catch {
      setIsAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    // Snapshot der localStorage-Favs an den initialen Mount-Zeitpunkt → kein stale-closure.
    let local = [];
    try { local = JSON.parse(localStorage.getItem(LS_KEY_GUEST_FAVS) || '[]'); } catch { /* noop */ }
    hydrateFromServer(local);
  }, [hydrateFromServer]);

  // Persist guest favs locally whenever they change AND no auth
  useEffect(() => {
    if (!isAuthenticated) {
      try { localStorage.setItem(LS_KEY_GUEST_FAVS, JSON.stringify(favIds)); } catch { /* noop */ }
    }
  }, [favIds, isAuthenticated]);

  // Persist compare list
  useEffect(() => {
    try { localStorage.setItem(LS_KEY_COMPARE, JSON.stringify(compareIds)); } catch { /* noop */ }
  }, [compareIds]);

  const isFavorited = useCallback((productId) => favIds.includes(productId), [favIds]);

  const toggleFavorite = useCallback(async (productId) => {
    const wasFav = favIds.includes(productId);
    // Optimistic update
    setFavIds((cur) => wasFav ? cur.filter((id) => id !== productId) : [...cur, productId]);
    if (!isAuthenticated) return;
    try {
      if (wasFav) {
        await axios.delete(`${API}/me/favorites/${productId}`, { withCredentials: true });
      } else {
        await axios.post(`${API}/me/favorites`, { product_id: productId }, { withCredentials: true });
      }
    } catch {
      // Roll back on failure
      setFavIds((cur) => wasFav ? [...cur, productId] : cur.filter((id) => id !== productId));
    }
  }, [favIds, isAuthenticated]);

  const isCompared = useCallback((productId) => compareIds.includes(productId), [compareIds]);

  const toggleCompare = useCallback((productId) => {
    setCompareIds((cur) => {
      if (cur.includes(productId)) return cur.filter((id) => id !== productId);
      if (cur.length >= MAX_COMPARE) return cur; // cap at 3
      return [...cur, productId];
    });
  }, []);

  const clearCompare = useCallback(() => setCompareIds([]), []);

  const value = useMemo(() => ({
    favIds, isFavorited, toggleFavorite,
    compareIds, isCompared, toggleCompare, clearCompare,
    reloadFromServer, isAuthenticated, MAX_COMPARE,
  }), [favIds, isFavorited, toggleFavorite, compareIds, isCompared, toggleCompare, clearCompare, reloadFromServer, isAuthenticated]);

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
};

export default FavoritesProvider;
